// src/components/issues/CarteGroupe.tsx — SCE Issues
// Multi-équipements (Lot 14, option C) sur la fiche problème :
//   - la carte « Même défaut » : les fiches sœurs, leur machine et leur statut,
//     et « Ajouter un équipement » (coordinateur du métier ou admin) ;
//   - sans fiche sœur, PAS de carte (30/09) : le cas est rare, une carte
//     entière pour une question prenait trop de place. Un lien discret en bas
//     de la carte Équipement (`LienAutreMachine`) ouvre la même saisie ;
//   - la case « Appliquer aux N machines », posée à côté des actions qui
//     peuvent valoir pour tout le groupe (qualifier, annuler, clore, commenter).

import { useState, type JSX } from 'react'
import { ChevronRight, Plus } from 'lucide-react'
import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import { lienProbleme } from '@/utils/liensFiches'
import { libelleMachines } from '@/utils/groupes'
import type { Equipement, Issue } from '@/types/issue.types'
import { AjoutMachines } from './AjoutMachines'
import { STYLE_CARTE, STYLE_TITRE_CARTE } from './CarteFiche'
import { EtatProbleme } from '@/components/ui/EtatProbleme'

export function CarteGroupe({
  issue,
  membres,
  peutAjouter,
  onAjouter,
  enCours,
}: {
  issue: Issue
  /** Les AUTRES fiches du groupe (utils/groupes, `membresDuGroupe`). */
  membres: readonly Issue[]
  /** Coordinateur du métier ou administrateur, problème actif. */
  peutAjouter: boolean
  onAjouter: (eq: Equipement) => Promise<void>
  enCours: boolean
}): JSX.Element | null {
  const ouvrirFiche = useOuvrirFiche()
  const [ajout, setAjout] = useState(false)

  // Fiche seule : pas de carte, le lien discret de la carte Équipement suffit.
  if (membres.length === 0) return null

  return (
    <section style={STYLE_CARTE}>
      <h3 style={STYLE_TITRE_CARTE}>
        {membres.length > 0 ? `Même défaut sur ${libelleMachines(membres.length)} de plus` : 'Autres équipements'}
      </h3>
      {membres.map((m) => (
        <button
          key={m.id}
          type="button"
          onClick={() => ouvrirFiche(lienProbleme(m.id))}
          aria-label={`Ouvrir ${m.id}, équipement ${m.numeroSerie}`}
          className="flex items-center gap-3 w-full text-left"
          style={{
            minHeight: 52,
            padding: '6px 0',
            border: 'none',
            borderTop: '1px solid var(--color-gray-10)',
            background: 'transparent',
            color: 'var(--color-black)',
          }}
        >
          <span className="flex flex-col flex-1" style={{ minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{m.numeroSerie || '—'}</span>
            <span style={{ fontSize: 13, color: 'var(--color-gray-70)' }}>
              {[m.id, m.pn !== issue.pn ? `Projet ${m.pn}` : ''].filter(Boolean).join(' · ')}
            </span>
          </span>
          <EtatProbleme statut={m.statut} criticite={m.criticite} />
          <ChevronRight size={18} color="var(--color-gray-60)" />
        </button>
      ))}

      {peutAjouter && !ajout && (
        <button
          type="button"
          onClick={() => setAjout(true)}
          disabled={enCours}
          className="flex items-center gap-2 w-full"
          style={{
            minHeight: 44,
            padding: 0,
            border: 'none',
            borderTop: '1px solid var(--color-gray-10)',
            background: 'transparent',
            color: 'var(--color-black)',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          <Plus size={16} />
          Ajouter un équipement
        </button>
      )}
      {peutAjouter && ajout && (
        <SaisieAjout issue={issue} membres={membres} onAjouter={onAjouter} onFermer={() => setAjout(false)} enCours={enCours} />
      )}
    </section>
  )
}

/**
 * Fiche seule (30/09) : « Autre équipement concerné ? » en petit, en bas de la
 * carte Équipement — la question reste posée sans occuper une carte.
 */
export function LienAutreMachine({
  issue,
  onAjouter,
  enCours,
}: {
  issue: Issue
  onAjouter: (eq: Equipement) => Promise<void>
  enCours: boolean
}): JSX.Element {
  const [ajout, setAjout] = useState(false)
  if (ajout) {
    return (
      <div style={{ borderTop: '1px solid var(--color-gray-10)', paddingTop: 8 }}>
        <SaisieAjout issue={issue} membres={[]} onAjouter={onAjouter} onFermer={() => setAjout(false)} enCours={enCours} />
      </div>
    )
  }
  return (
    <button
      type="button"
      onClick={() => setAjout(true)}
      disabled={enCours}
      className="flex items-center gap-2 w-full"
      style={{
        minHeight: 40,
        padding: 0,
        border: 'none',
        borderTop: '1px solid var(--color-gray-10)',
        background: 'transparent',
        color: 'var(--color-gray-70)',
        fontSize: 13,
      }}
    >
      <Plus size={14} />
      Autre équipement concerné ?
    </button>
  )
}

/** Recherche de la machine à ajouter, commune à la carte et au lien. */
function SaisieAjout({
  issue,
  membres,
  onAjouter,
  onFermer,
  enCours,
}: {
  issue: Issue
  membres: readonly Issue[]
  onAjouter: (eq: Equipement) => Promise<void>
  onFermer: () => void
  enCours: boolean
}): JSX.Element {
  return (
    <div style={{ padding: '8px 0 12px' }}>
      <AjoutMachines
        pn={issue.pn}
        exclus={[issue.numeroSerie, ...membres.map((m) => m.numeroSerie)]}
        onAjouter={(eq) => {
          void onAjouter(eq).then(onFermer)
        }}
        desactive={enCours}
      />
      <p style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)', margin: '8px 0 0' }}>
        Une nouvelle fiche, même titre et même description
        {issue.statut === 'En cours' ? ', qualifiée comme celle-ci' : ''}.
      </p>
      <button
        type="button"
        onClick={onFermer}
        disabled={enCours}
        style={{ minHeight: 44, border: 'none', background: 'transparent', fontSize: 'var(--font-size-info)', textDecoration: 'underline' }}
      >
        Annuler
      </button>
    </div>
  )
}

/**
 * « Appliquer aussi aux 2 autres machines » — cochée d'office pour qualifier,
 * annuler (réponse des coordinateurs : « oui, décochable ») et commenter
 * (03/11). La clôture n'a plus de case : elle demande Seulement / Tout le groupe.
 */
export function CaseGroupe({
  nombre,
  coche,
  onChange,
  libelle,
  desactive,
}: {
  /** Fiches sœurs concernées par l'action. 0 : rien n'est affiché. */
  nombre: number
  coche: boolean
  onChange: (v: boolean) => void
  /** Verbe de l'action : « Qualifier aussi », « Publier aussi »… */
  libelle: string
  desactive?: boolean
}): JSX.Element | null {
  if (nombre === 0) return null
  return (
    <label
      className="flex items-center gap-3"
      style={{ minHeight: 44, fontSize: 'var(--font-size-copy2)', cursor: desactive ? 'default' : 'pointer' }}
    >
      <input
        type="checkbox"
        checked={coche}
        onChange={(e) => onChange(e.target.checked)}
        disabled={desactive}
        style={{ width: 20, height: 20, accentColor: 'var(--color-black)' }}
      />
      {libelle} {nombre > 1 ? `les ${libelleMachines(nombre)}` : 'l’autre équipement'} du groupe
    </label>
  )
}
